import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, User, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { lovable } from "@/integrations/lovable/index";

const Signup = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { signUp, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate("/");
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: t.common.error, description: t.signup.passwordMismatch, variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: t.common.error, description: t.signup.passwordShort, variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    const { error } = await signUp(email, password, fullName, phone);
    if (error) {
      toast({ title: t.signup.failed, description: error.message, variant: "destructive" });
      setIsSubmitting(false);
      return;
    }
    toast({ title: t.signup.success, description: t.signup.successDesc });
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const iconClass = "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground rtl:left-auto rtl:right-3";
  const inputClass = "pl-10 rtl:pl-3 rtl:pr-10";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 pb-20 px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-serif mb-2">{t.signup.title}</h1>
            <p className="text-muted-foreground">{t.signup.subtitle}</p>
          </div>
          <div className="bg-card border border-border p-8 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <User className={iconClass} />
                <Input type="text" placeholder={t.signup.fullName} value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} required disabled={isSubmitting} />
              </div>
              <div className="relative">
                <Mail className={iconClass} />
                <Input type="email" placeholder={t.signup.emailPlaceholder} value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required disabled={isSubmitting} />
              </div>
              <div className="relative">
                <Phone className={iconClass} />
                <Input type="tel" placeholder={t.signup.phonePlaceholder} value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} required disabled={isSubmitting} />
              </div>
              <div className="relative">
                <Lock className={iconClass} />
                <Input type="password" placeholder={t.signup.passwordPlaceholder} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} required disabled={isSubmitting} />
              </div>
              <div className="relative">
                <Lock className={iconClass} />
                <Input type="password" placeholder={t.signup.confirmPassword} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass} required disabled={isSubmitting} />
              </div>
              <Button type="submit" variant="elegant" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.signup.creating}</>) : t.signup.createBtn}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{language === "ar" ? "أو" : "OR"}</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
              onClick={async () => {
                const { error } = await lovable.auth.signInWithOAuth("google", {
                  redirect_uri: window.location.origin,
                });
                if (error) {
                  toast({ title: t.common.error, description: error.message, variant: "destructive" });
                }
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {language === "ar" ? "التسجيل بحساب Google" : "Sign up with Google"}
            </Button>

            <p className="text-center text-sm text-muted-foreground mt-6">
              {t.signup.hasAccount}{" "}
              <Link to="/login" className="text-primary hover:underline">{t.signup.signIn}</Link>
            </p>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default Signup;
